/* eslint-disable id-length */
/* eslint-disable max-lines */
/* eslint-disable react/prop-types */
/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';
import {
  fetchAlreadyLoggedEfforts,
  fetchProjectOptions,
  fetchActivityOptions,
  bulkAddEfforts,
  updateLoggedEffort,
  getEffortLoggingDates,
} from '../../../Api/request/timeSheetReport';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import './addEffortModal.scss';
import DateRangeInput from '../../../Components/DateRangeInput/DateRangeInput';

dayjs.extend(customParseFormat);

const STORAGE_KEY = 'tsr-add-effort-cache-v1';
const DEFAULT_YET_TO_SUBMIT_ROWS = [];
const DEFAULT_ALREADY_LOGGED_ROWS = [];
const DEFAULT_FORM = {
  date: '',
  dateObj: null,
  project: null,
  effortHours: '',
  activity: null,
  comments: '',
};

const getValueByPath = (obj, path) => path.split('.').reduce((acc, key) => acc?.[key], obj);

const EllipsisCell = ({ value }) => {
  const textRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [value]);

  return (
    <span
      ref={textRef}
      className="cell-text"
      title={isTruncated ? value : undefined}
    >
      {value}
    </span>
  );
};

const getCandidateValue = (rowData, candidates) => {
  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    const value = candidate.includes('.')
      ? getValueByPath(rowData, candidate)
      : rowData?.[candidate];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
};

const resolveTimeSheetId = (rowData) => String(getCandidateValue(rowData, [
  'timeSheetId',
  'timesheetId',
  'time_sheet_id',
  'timesheet_id',
  'id',
  'data_id',
]));

const pickFirstArray = (obj, paths) => {
  for (let index = 0; index < paths.length; index += 1) {
    const value = getValueByPath(obj, paths[index]);
    if (Array.isArray(value)) return value;
  }
  return [];
};

const formatDisplayDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const parseEffortHours = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatEffortHours = (value) => String(parseFloat(value.toFixed(2)));

const normalizeCommentValue = (value) => String(value || '-').trim() || '-';

const buildEffortMatchKey = (row) => [
  String(row?.project_id || row?.projectName || ''),
  String(row?.activity_id || row?.activity || ''),
  String(row?.date || ''),
  normalizeCommentValue(row?.comments),
].join('::');

// Fix: Ensure we flag "success: false" from payloads, not just 400+ statuses
const hasErrorResponse = (response) => {
  if (!response) return true;
  if (response?.status && response.status >= 400) return true;
  if (response?.success === false || response?.data?.success === false) return true;
  return false;
};

const getTodayDateLabel = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = today.toLocaleString('en-US', { month: 'short' });
  const year = today.getFullYear();
  const dayName = today.toLocaleString('en-US', { weekday: 'long' });
  return `Date: ${day}-${month}-${year} (${dayName})`;
};
const getDateLabel = (value) => {
  const currentDate = dayjs.isDayjs(value) ? value : dayjs(value);
  if (!currentDate.isValid()) return getTodayDateLabel();

  return `Date: ${currentDate.format('DD-MMM-YYYY')} (${currentDate.format('dddd')})`;
};

const normalizeYetToSubmitRows = (rows) => rows.map((row, index) => ({
  id: row?.id || row?.data_id || `y-${index}`,
  date: formatDisplayDate(row?.date || row?.logDate || row?.efforts_date) || '-',
  day: row?.day || row?.dayName || row?.day_of_effort || '-',
  project_id: String(row?.project_id || row?.p_id || row?.do || row?.project?.project_id || ''),
  activity_id: String(row?.activity_id || row?.a_id || ''),
  projectName: row?.projectName || row?.project || row?.project?.project_name || row?.['project.project_name'] || '-',
  effortHours: row?.effortHours || row?.effort || row?.efforts || row?.hours || '-',
  activity: row?.activity || '-',
  comments: row?.comments || '-',
}));

const normalizeAlreadyLoggedRows = (rows) => rows.map((row, index) => ({
  id: resolveTimeSheetId(row) || row?.id || row?.data_id || `a-${index}`,
  timeSheetId: resolveTimeSheetId(row),
  date: formatDisplayDate(row?.date || row?.logDate || row?.efforts_date) || '-',
  day: row?.day || row?.dayName || row?.day_of_effort || '-',
  project_id: String(row?.project_id || row?.p_id || row?.do || row?.project?.project_id || ''),
  activity_id: String(row?.activity_id || row?.a_id || ''),
  projectName: row?.projectName || row?.project || row?.project?.project_name || row?.['project.project_name'] || '-',
  effortHours: row?.effortHours || row?.effort || row?.efforts || row?.hours || '-',
  activity: row?.activity || '-',
  comments: row?.comments || '-',
  source: row?.source,
  issue_status: row?.issue_status,
}));

const normalizeOptionList = (response, optionType) => {
  const payload = response?.data;
  const root = payload?.data || payload;
  const nestedData = root?.data;
  const list = Array.isArray(nestedData)
    ? nestedData
    : Array.isArray(root)
      ? root
      : [];

  if (optionType === 'project_name') {
    return list.map((item) => ({
      value: String(item?.project_id || item?.id || ''),
      label: item?.project_name || item?.name || '-',
    }));
  }

  return list.map((item) => ({
    value: String(item?.activity_id || item?.id || item?.value || ''),
    label: item?.activity || item?.activity_name || item?.label || '-',
  }));
};

const getCachedData = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (e) {
    return null;
  }
};

const EffortGrid = ({ columns, rows, emptyText = 'No data available', className = '' }) => (
  <div
    className={`add-effort-grid ${className === 'already-added-grid' ? 'already-logged-effort-grid' : ''}`}
    style={{
      gridTemplateColumns: columns
        .map((column) => (String(column.width).includes('fr') ? `minmax(0, ${column.width})` : column.width))
        .join(' '),
    }}
  >
    <div className="add-effort-grid-row add-effort-grid-row-head">
      {columns.map((column) => (
        <div key={column.key} className={`add-effort-grid-cell add-effort-grid-head 
        ${column.key === 'effortHours' || column.key === 'Effort (Hrs)' ? ' add-effort-grid-cell-effort' : ''}`}>
          {column.label}
        </div>
      ))}
    </div>

    {rows.length === 0 && (
      <div
        className="add-effort-grid-empty"
        style={{ gridColumn: `1 / span ${columns.length}` }}
      >
        {emptyText}
      </div>
    )}

    {rows.map((row, rowIndex) => (
      <div
        key={row.id}
        className={`add-effort-grid-row ${
          rowIndex % 2 === 0
            ? 'add-effort-grid-row-even'
            : 'add-effort-grid-row-odd'
        }`}
      >
        {columns.map((column) => {
          const isActionColumn = column.key === 'action';
          const isEffortColumn = column.key === 'effortHours';

          return (
            <div
              key={`${row.id}-${column.key}`}
              className={`add-effort-grid-cell
            ${isActionColumn ? 'add-effort-grid-cell-action' : ''}
            ${isEffortColumn ? 'add-effort-grid-cell-effort' : ''}
          `}
            >
              {column.render ? (
                column.render(row)
              ) : (
                <EllipsisCell value={String(row[column.key] || '')} />
              )}
            </div>
          );
        })}
      </div>
    ))}
  </div>
);

const AddEffortModal = ({ open, onClose, onSubmit, authToken, employeeId, userInfo, tableViewType }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [editingRowId, setEditingRowId] = useState(null);

  const [yetToSubmitRows, setYetToSubmitRows] = useState(() => {
    const cached = getCachedData();
    return cached?.yetToSubmitRows || DEFAULT_YET_TO_SUBMIT_ROWS;
  });
  const [alreadyLoggedRows, setAlreadyLoggedRows] = useState(() => {
    const cached = getCachedData();
    return cached?.alreadyLoggedRows || DEFAULT_ALREADY_LOGGED_ROWS;
  });
  const [loggedDateLabel, setLoggedDateLabel] = useState(() => {
    const cached = getCachedData();
    return cached?.loggedDateLabel || getTodayDateLabel();
  });

  const [projectOptions, setProjectOptions] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [effortLoggingDates, setEffortLoggingDates] = useState(null);

  const modalRef = useRef(null);
  const alreadyLoggedRequestId = useRef(0);

  const projectDropdownOptions = useMemo(
    () => projectOptions.map((option) => ({ id: option.value, name: option.label })),
    [projectOptions],
  );

  const activityDropdownOptions = useMemo(
    () => activityOptions.map((option) => ({ id: option.value, name: option.label })),
    [activityOptions],
  );

  const selectedProjectOption = useMemo(
    () => projectDropdownOptions.find(
      (option) => String(option.id) === String(formData.project) || option.name === formData.project,
    ) || null,
    [projectDropdownOptions, formData.project],
  );

  const selectedActivityOption = useMemo(
    () => activityDropdownOptions.find(
      (option) => String(option.id) === String(formData.activity) || option.name === formData.activity,
    ) || null,
    [activityDropdownOptions, formData.activity],
  );

  const minSelectableDate = effortLoggingDates?.minDate;
  const maxSelectableDate = effortLoggingDates?.maxDate;
  const isSelectableRangeSameYear = effortLoggingDates
    && minSelectableDate.getFullYear() === maxSelectableDate.getFullYear();

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;
    setEffortLoggingDates(null);

    const loadEffortLoggingDates = async () => {
      try {
        const response = await getEffortLoggingDates({ authToken });
        if (!isActive || hasErrorResponse(response)) return;

        const payload = response?.data?.data;
        const configuration = Array.isArray(payload) ? payload[0] : payload;
        const minDate = dayjs(configuration?.start_date, 'YYYY-MM-DD', true);
        const maxDate = dayjs(configuration?.end_date, 'YYYY-MM-DD', true);
        if (!minDate.isValid() || !maxDate.isValid() || minDate.isAfter(maxDate, 'day')) return;

        setEffortLoggingDates({ minDate: minDate.toDate(), maxDate: maxDate.toDate() });
      } catch (error) {
        if (isActive) setEffortLoggingDates(null);
      }
    };

    loadEffortLoggingDates();

    return () => {
      isActive = false;
    };
  }, [open, authToken]);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const validateUserInput = (data) => {
    const rawHours = String(data?.effortHours || '').trim();
    const isNumeric = /^[0-9]+(\.[0-9]+)?$/.test(rawHours);
    if (!isNumeric) return false;

    const hours = parseFloat(rawHours);
    return hours > 0 && (hours % 0.25) === 0;
  };

  useEffect(() => {
    if (!open) return;

    if (yetToSubmitRows.length > 0) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ yetToSubmitRows, alreadyLoggedRows, loggedDateLabel }),
      );
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [yetToSubmitRows, alreadyLoggedRows, loggedDateLabel, open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleModalClose(event);
      }
    };

    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        const isDropdownClick = event.target.closest('.add-effort-search-dropdown')
          || event.target.closest('.dropdown-menu')
          || event.target.closest('[class*="dropdown"]')
          || event.target.closest('[class*="popup"]')
          || event.target.closest('.MuiPopover-root')
          || event.target.closest('.p-dropdown-panel');

        if (!isDropdownClick) {
          handleModalClose(event);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [open]);

  const loadAlreadyLoggedEfforts = useCallback(async (selectedDate = new Date()) => {
    const requestId = alreadyLoggedRequestId.current + 1;
    alreadyLoggedRequestId.current = requestId;
    const targetDate = dayjs.isDayjs(selectedDate) ? selectedDate.toDate() : selectedDate;
    try {
      const response = await fetchAlreadyLoggedEfforts({
        authToken,
        employeeId,
        startDate: formatDate(targetDate),
        endDate: formatDate(targetDate),
        setLoading,
        userInfo,
        tableViewType,
      });

      if (!response?.status || response.status >= 400) {
        throw new Error('Unable to fetch already logged efforts');
      }

      // A slower request for the previously selected date must not replace the latest results.
      if (requestId !== alreadyLoggedRequestId.current) return;

      const payload = response?.data;
      const root = payload?.data || payload;
      const directRows = Array.isArray(root) ? root : [];

      const yetRows = normalizeYetToSubmitRows(
        pickFirstArray(Array.isArray(root) ? {} : root, [
          'yetToSubmitEfforts',
          'yetToSubmit',
          'yetToLog',
          'pendingEfforts',
          'data.yetToSubmitEfforts',
        ]),
      );

      const alreadyRowsFromNamedKeys = normalizeAlreadyLoggedRows(
        pickFirstArray(Array.isArray(root) ? {} : root, [
          'alreadyLoggedEfforts',
          'alreadyLogged',
          'loggedEfforts',
          'data.alreadyLoggedEfforts',
          'data',
        ]),
      );

      const alreadyRowsFromDirectData = normalizeAlreadyLoggedRows(directRows);
      const alreadyRows = alreadyRowsFromNamedKeys.length > 0 ? alreadyRowsFromNamedKeys : alreadyRowsFromDirectData;

      const resolvedDateLabel
        = (Array.isArray(root) ? undefined : root?.selectedDateLabel)
        || (Array.isArray(root) ? undefined : root?.loggedDateLabel)
        || (Array.isArray(root) ? undefined : root?.selectedDate)
        || (Array.isArray(root) ? undefined : root?.dateLabel)
        || (directRows[0]?.efforts_date
          ? `${formatDisplayDate(directRows[0]?.efforts_date)} (${directRows[0]?.day_of_effort || '-'})` : undefined)
        || getDateLabel(targetDate);

      if (yetRows.length > 0) {
        setYetToSubmitRows(yetRows);
      }
      setAlreadyLoggedRows(alreadyRows);
      setLoggedDateLabel(resolvedDateLabel.startsWith('Date:') ? resolvedDateLabel : `Date: ${resolvedDateLabel}`);
    } catch (error) {
      // Catch layout errors gracefully
    }
  }, [authToken, employeeId, userInfo, tableViewType]);

  const loadDropdownOptions = useCallback(async () => {
    try {
      const [projectResponse, activityResponse] = await Promise.all([
        fetchProjectOptions({ authToken, employeeId, userInfo }),
        fetchActivityOptions({ authToken, employeeId, userInfo }),
      ]);

      setProjectOptions(normalizeOptionList(projectResponse, 'project_name'));
      setActivityOptions(normalizeOptionList(activityResponse, 'activities'));
    } catch (error) {
      setProjectOptions([]);
      setActivityOptions([]);
    }
  }, [authToken, employeeId, userInfo]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    loadAlreadyLoggedEfforts();
    loadDropdownOptions();
  }, [open, loadAlreadyLoggedEfforts, loadDropdownOptions]);

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const getOptionNameById = (optionList, optionId) => {
    return optionList.find((option) => String(option.id) === String(optionId))?.name || '-';
  };

  const handleProjectChange = (event) => {
    setFormData((prev) => ({ ...prev, project: event.target.value?.id || null }));
  };

  const handleActivityChange = (event) => {
    setFormData((prev) => ({ ...prev, activity: event.target.value?.id || null }));
  };

  const findMatchingAlreadyLoggedRow = (newRow, excludedTimeSheetId = '') => alreadyLoggedRows.find((row) => {
    if (excludedTimeSheetId && String(row.timeSheetId) === String(excludedTimeSheetId)) {
      return false;
    }

    return (
      buildEffortMatchKey(row) === buildEffortMatchKey(newRow)
      || (
        row.projectName === newRow.projectName
        && row.activity === newRow.activity
        && row.date === newRow.date
        && normalizeCommentValue(row.comments) === normalizeCommentValue(newRow.comments)
      )
    );
  });

  const handleAddRow = () => {
    const activeEditingRowId = editingRowId;
    const editingRow = activeEditingRowId
      ? yetToSubmitRows.find((row) => row.id === activeEditingRowId) || null
      : null;
    const hasDate = formData.dateObj && formData.dateObj.isValid?.();
    const hasProject = formData.project && formData.project !== '';
    const hasActivity = formData.activity && formData.activity !== '';
    const hasHours = formData.effortHours;
    const hasComments = String(formData.comments || '').trim();
    const isEffortValid = validateUserInput({ effortHours: formData.effortHours });

    if (!hasDate || !hasProject || !hasHours || !hasActivity || !isEffortValid || !hasComments) {
      if (!hasDate) setErrorMessage('Please select a valid date.');
      else if (!hasProject) setErrorMessage('Please select a project.');
      else if (!hasActivity) setErrorMessage('Please select an activity.');
      else if (!hasHours) setErrorMessage('Please enter effort hours.');
      else if (!isEffortValid) setErrorMessage('Please enter valid effort hours.');
      else if (!hasComments) setErrorMessage('Please enter comments.');
      return;
    }

    const dateValue = formData.dateObj.format('DD-MMM-YYYY');
    const newEffort = parseEffortHours(formData.effortHours);

    // Calculate total for this date in already logged rows
    let totalAlreadyLogged = 0;
    alreadyLoggedRows.forEach(row => {
      // Skip the row if we are currently editing it (it will be replaced)
      if (activeEditingRowId && editingRow?.timeSheetId
        && String(row.timeSheetId) === String(editingRow.timeSheetId)) return;
      if (row.date === dateValue) {
        totalAlreadyLogged += parseEffortHours(row.effortHours);
      }
    });

    // Calculate total for this date in yet to submit rows
    let totalYetToSubmit = 0;
    yetToSubmitRows.forEach(row => {
      // Skip the row being edited
      if (activeEditingRowId && row.id === activeEditingRowId) return;
      if (row.date === dateValue) {
        totalYetToSubmit += parseEffortHours(row.effortHours);
      }
    });

    // 24 Hour Limit Validation
    if (totalAlreadyLogged + totalYetToSubmit + newEffort > 24) {
      setErrorMessage(`Total effort for ${dateValue} cannot exceed 24 hours. 
        (Currently Logged: ${totalAlreadyLogged + totalYetToSubmit}h, Trying to Add: ${newEffort}h)`);
      return;
    }

    const projectName = getOptionNameById(projectDropdownOptions, formData.project);
    const activityName = getOptionNameById(activityDropdownOptions, formData.activity);

    const dayName = formData.dateObj.format('dddd');

    const newRow = {
      id: activeEditingRowId || `new-${Date.now()}`,
      project_id: String(formData.project || ''),
      activity_id: String(formData.activity || ''),
      date: dateValue,
      day: dayName,
      projectName,
      effortHours: formData.effortHours,
      activity: activityName,
      comments: normalizeCommentValue(formData.comments),
      timeSheetId: editingRow?.timeSheetId || '',
      source: editingRow?.source,
      issue_status: editingRow?.issue_status,
    };

    setYetToSubmitRows((prev) => {
      const rowsExcludingEdited = activeEditingRowId
        ? prev.filter((row) => row.id !== activeEditingRowId)
        : prev;

      const matchingPendingIndex = rowsExcludingEdited.findIndex((row) => {
        const isSameTimeSheet = newRow.timeSheetId && row.timeSheetId
          && String(row.timeSheetId) === String(newRow.timeSheetId);

        return isSameTimeSheet || buildEffortMatchKey(row) === buildEffortMatchKey(newRow);
      });

      if (matchingPendingIndex >= 0) {
        const targetRow = rowsExcludingEdited[matchingPendingIndex];
        const mergedRow = {
          ...targetRow,
          ...newRow,
          id: targetRow.id,
          timeSheetId: targetRow.timeSheetId || newRow.timeSheetId,
          effortHours: formatEffortHours(
            parseEffortHours(targetRow.effortHours) + parseEffortHours(newRow.effortHours),
          ),
          comments: normalizeCommentValue(targetRow.comments || newRow.comments),
          source: targetRow.source ?? newRow.source ?? 1,
          issue_status: targetRow.issue_status ?? newRow.issue_status ?? null,
        };

        return rowsExcludingEdited.map((row, index) => (index === matchingPendingIndex ? mergedRow : row));
      }

      const matchingLoggedRow = findMatchingAlreadyLoggedRow(newRow, editingRow?.timeSheetId);

      if (matchingLoggedRow) {
        const previousEffort = activeEditingRowId && editingRow?.timeSheetId === matchingLoggedRow.timeSheetId
          ? 0
          : parseEffortHours(matchingLoggedRow.effortHours);

        return [{
          ...matchingLoggedRow,
          ...newRow,
          id: activeEditingRowId || `update-${matchingLoggedRow.timeSheetId || Date.now()}`,
          timeSheetId: matchingLoggedRow.timeSheetId,
          effortHours: formatEffortHours(previousEffort + parseEffortHours(newRow.effortHours)),
          comments: normalizeCommentValue(newRow.comments || matchingLoggedRow.comments),
          source: matchingLoggedRow.source ?? newRow.source ?? 1,
          issue_status: matchingLoggedRow.issue_status ?? newRow.issue_status ?? null,
        }, ...rowsExcludingEdited];
      }

      return activeEditingRowId ? [...rowsExcludingEdited, newRow] : [newRow, ...rowsExcludingEdited];
    });

    setEditingRowId(null);
    setFormData(DEFAULT_FORM);
    setErrorMessage('');
  };

  const handleEditYetRow = (row) => {
    const rowId = row?.id || null;
    setEditingRowId((prev) => {
      if (prev !== rowId) handlePrefillFromRow(row);
      return prev !== rowId ? rowId : null;
    });
  };

  const handlePrefillFromRow = (row, onlyDateAndProject = false) => {
    const parsedDate = row?.date ? dayjs(row.date, 'DD-MMM-YYYY') : null;
    const matchedProject = projectDropdownOptions.find((option) => option.name === row?.projectName);
    const matchedActivity = activityDropdownOptions.find((option) => option.name === row?.activity);

    setFormData({
      date: row?.date || '',
      dateObj: parsedDate?.isValid?.() ? parsedDate : null,
      project: row?.project_id || matchedProject?.id || row?.projectName || '',
      effortHours: onlyDateAndProject ? '' : String(row?.effortHours || ''),
      activity: onlyDateAndProject ? '' : (row?.activity_id || matchedActivity?.id || row?.activity || ''),
      comments: onlyDateAndProject ? '' : String(row?.comments || ''),
    });
  };

  const handleDeleteYetRow = (id) => {
    setYetToSubmitRows((prev) => prev.filter((row) => row.id !== id));
    setEditingRowId((prev) => (prev === id ? null : prev));
  };

  const handleCancelEditing = () => {
    setEditingRowId(null);
    setFormData(DEFAULT_FORM);
  };

  const refreshAlreadyLoggedEfforts = useCallback(async () => {
    const now = new Date();
    try {
      const response = await fetchAlreadyLoggedEfforts({
        authToken,
        employeeId,
        startDate: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        endDate: formatDate(now),
        setLoading,
        userInfo,
        tableViewType,
      });

      if (response?.status && response.status < 400) {
        const payload = response?.data;
        const root = payload?.data || payload;
        const directRows = Array.isArray(root) ? root : [];
        const alreadyRowsFromNamedKeys = normalizeAlreadyLoggedRows(
          pickFirstArray(Array.isArray(root) ? {} : root, [
            'alreadyLoggedEfforts', 'alreadyLogged', 'loggedEfforts', 'data.alreadyLoggedEfforts', 'data',
          ]),
        );
        const alreadyRows = alreadyRowsFromNamedKeys.length > 0
          ? alreadyRowsFromNamedKeys : normalizeAlreadyLoggedRows(directRows);
        setAlreadyLoggedRows(alreadyRows);
        return alreadyRows;
      }
    } catch (error) {
      // Silently fail
    }
    return alreadyLoggedRows;
  }, [authToken, employeeId, alreadyLoggedRows, tableViewType, userInfo]);

  const handleModalClose = (e) => {
    setErrorMessage('');
    if (onClose) {
      onClose(e);
    }
  };

  const handleEffortChange = (event) => {
    const value = event.target.value;

    // Allow empty input so users can backspace/clear the field
    if (value === '') {
      updateField('effortHours', '');
      return;
    }

    const numericValue = parseFloat(value);

    // Only update state if within 0 - 24 range
    if (numericValue >= 0 && numericValue <= 24) {
      updateField('effortHours', value);
    }
  };

  const handleSubmit = async () => {
    if (!yetToSubmitRows || yetToSubmitRows.length === 0) return;

    try {
      const recordsToInsert = [];
      const recordsToUpdate = [];

      yetToSubmitRows.forEach((entry, index) => {
        const resolvedProjectOption = projectDropdownOptions.find(
          (o) => String(o.id) === String(entry?.project_id)
          || o.name === entry?.projectName || o.name === entry?.project_id,
        );
        const resolvedActivityOption = activityDropdownOptions.find(
          (o) => String(o.id) === String(entry?.activity_id)
          || o.name === entry?.activity || o.name === entry?.activity_id,
        );

        const projectId = entry.project_id ?? resolvedProjectOption?.id;
        const parsedEffortDate = dayjs(entry.date, ['DD-MMM-YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'], true);
        const effortDate = parsedEffortDate.isValid() ? parsedEffortDate.format('YYYY-MM-DD') : '';
        const activityId = entry.activity_id ?? resolvedActivityOption?.id;
        const effort = entry.effortHours;

        if (!validateUserInput({ effortHours: effort })) {
          toast.error(`Invalid effort at index ${index}: ${effort}. Must be positive increments of 0.25.`);
          throw new Error('Invalid effort hours.');
        }

        const comment = entry.comments;
        const missingFields = [];
        if (!projectId) missingFields.push('project_id');
        if (!effortDate) missingFields.push('efforts_date');
        if (!activityId) missingFields.push('activity_id');
        if (!effort && effort !== 0) missingFields.push('efforts');
        if (!comment) missingFields.push('comments');

        if (missingFields.length) {
          throw new Error(`Invalid data at index ${index}: missing ${missingFields.join(', ')}`);
        }

        if (entry.timeSheetId) {
          recordsToUpdate.push({
            timeSheetId: String(entry.timeSheetId),
            effortData: {
              project_id: String(projectId),
              employee_id: String(employeeId || ''),
              efforts_date: String(effortDate),
              activity: String(activityId),
              efforts: String(effort),
              comments: String(comment),
              source: Number(entry?.source ?? 1),
              issue_status: entry?.issue_status ?? null,
            },
          });
          return;
        }

        recordsToInsert.push({
          project_id: String(projectId),
          employee_id: String(employeeId || ''),
          efforts_date: String(effortDate),
          activity_id: String(activityId),
          efforts: String(effort),
          comments: String(comment),
          source: 1,
          issue_status: null,
        });
      });

      if (recordsToUpdate.length > 0) {
        const updateResponses = await Promise.all(recordsToUpdate.map((entry) => updateLoggedEffort({
          authToken,
          timeSheetId: entry.timeSheetId,
          effortData: entry.effortData,
          userInfo,
          tableViewType: tableViewType || 'list_view',
        })));

        const failedUpdate = updateResponses.find((response) => hasErrorResponse(response));

        if (failedUpdate) {
          // Read deep error messages generated from 500 crashes
          const serverMsg = failedUpdate?.response?.data?.message
          || failedUpdate?.data?.message || failedUpdate?.message;
          throw new Error(serverMsg || 'Failed to update efforts');
        }
      }

      if (recordsToInsert.length > 0) {
        const response = await bulkAddEfforts({
          authToken,
          employeeId,
          effortsData: recordsToInsert,
          userInfo,
          tableViewType: tableViewType || 'list_view',
        });

        if (hasErrorResponse(response)) {
          // Fix: Read deep error messages generated from 500 crashes
          const serverMsg = response?.response?.data?.message || response?.data?.message || response?.message;
          throw new Error(serverMsg || 'Failed to submit efforts');
        }
      }

      localStorage.removeItem(STORAGE_KEY);
      setYetToSubmitRows([]);
      const refreshedAlreadyRows = await refreshAlreadyLoggedEfforts();
      toast.success(recordsToUpdate.length > 0 && recordsToInsert.length === 0
        ? 'Efforts updated successfully'
        : 'Efforts submitted successfully');
      onSubmit?.({ yetToSubmitRows: [], alreadyLoggedRows: refreshedAlreadyRows });
      handleModalClose();
    } catch (error) {
      // Aggressively dig for the message inside the 500 error response
      const submitErrorMessage = error?.response?.data?.message
        || error?.data?.message
        || error?.message
        || 'Error submitting efforts';

      toast.error(submitErrorMessage);
    }
  };

  const yetToSubmitColumns = useMemo(() => [
    { key: 'date', label: 'Date', width: '110px' },
    { key: 'day', label: 'Day', width: '110px' },
    { key: 'projectName', label: 'Project Name', width: '135px' },
    {
      key: 'effortHours',
      label: 'Effort (Hrs)',
      width: '115px',
      render: (row) => {
        const hours = parseFloat(row.effortHours);
        return Number.isNaN(hours) ? row.effortHours : hours.toFixed(2);
      },
    },
    { key: 'activity', label: 'Activity', width: '125px' },
    { key: 'comments', label: 'Comments', width: '170px' },
    {
      key: 'action',
      label: 'Action',
      width: '1fr',
      render: (row) => (
        <div className="add-effort-actions">
          {editingRowId === row.id ? (
            <span className="add-effort-editing-label">Editing...</span>
          ) : (
            <>
              <button type="button" aria-label="add row"
                onClick={() => handlePrefillFromRow(row, true)}><i className="pi pi-plus" /></button>
              <button type="button" aria-label="edit row"
                onClick={() => handleEditYetRow(row)}><i className="pi pi-pencil" /></button>
              <button type="button" aria-label="delete row"
                onClick={() => handleDeleteYetRow(row.id)}><i className="pi pi-trash" /></button>
            </>
          )}
        </div>
      ),
    },
  ], [editingRowId, projectDropdownOptions, activityDropdownOptions]);

  const alreadyLoggedColumns = useMemo(() => [
    { key: 'projectName', label: 'Project Name', width: '150px' },
    { key: 'effortHours', label: 'Effort (Hrs)', width: '110px' },
    { key: 'activity', label: 'Activity', width: '310px' },
    { key: 'comments', label: 'Comments', width: '1fr' },
  ], []);

  if (!open) return null;

  return (
    <div className="add-effort-modal-backdrop" role="presentation">
      <div className="add-effort-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Add Effort">
        <div className="add-effort-header">
          <h3>Add Effort</h3>
        </div>
        <div className="add-effort-error-message" role="alert" style={{ display: errorMessage ? 'block' : 'none' }}>
          {errorMessage}
        </div>
        <div className="add-effort-form-grid">
          <div className="dri-grid-placement" style={{ position: 'relative' }}>
            <DateRangeInput
              label="Date"
              showFromTo={false}
              minYear={minSelectableDate?.getFullYear()}
              maxYear={maxSelectableDate?.getFullYear()}
              minDay={minSelectableDate}
              maxDay={maxSelectableDate}
              // Month bounds are year-agnostic, so only apply them when the range stays within one year
              minMonth={isSelectableRangeSameYear ? minSelectableDate.getMonth() + 1 : undefined}
              maxMonth={isSelectableRangeSameYear ? maxSelectableDate.getMonth() + 1 : undefined}
              placeholder="Select Date"
              isClearable
              isRequired
              fromValue={formData.dateObj}
              onApply={(date) => {
                const formattedDate = date ? `${date.format('DD-MMM-YYYY')}` : '';
                setFormData((prev) => ({ ...prev, date: formattedDate, dateObj: date }));
                loadAlreadyLoggedEfforts(date || new Date());
              }}
              onReset={() => {
                setFormData((prev) => ({ ...prev, date: '', dateObj: null }));
                loadAlreadyLoggedEfforts(new Date());
              }}
            />
          </div>

          <label>
            <span className="required-label">Project Name<span className="asteric">*</span></span>
            <SearchDropdown
              options={projectDropdownOptions}
              placeholder="Select Project"
              value={selectedProjectOption}
              onChange={handleProjectChange}
              dropdownCss="add-effort-search-dropdown"
              required
            />
          </label>
          <label>
            <span className="effort-label-row">
              <span className="required-label">Efforts (Hrs)<span className="asteric">*</span></span>
              <span className="effort-info-wrapper">
                <button type="button" className="effort-info-btn" aria-label="Effort conversion information">i</button>
                <span className="effort-info-tooltip" role="tooltip">
                  <span>1 hr = 1</span>
                  <span>1 hr 15 min = 1.25</span>
                  <span>1 hr 30 min = 1.5</span>
                  <span>1 hr 45 min = 1.75</span>
                </span>
              </span>
            </span>
            <input
              type="number"
              step="0.25"
              placeholder="Enter Efforts"
              max={24}
              value={formData.effortHours}
              onChange={handleEffortChange}
              required
            />
          </label>
          <label>
            <span className="required-label">Activity<span className="asteric">*</span></span>
            <SearchDropdown
              label=""
              options={activityDropdownOptions}
              placeholder="Select Activity"
              value={selectedActivityOption}
              onChange={handleActivityChange}
              dropdownCss="add-effort-search-dropdown"
              searchBox={true}
            />
          </label>
          <label className="full-width">
            <span className="required-label">Comments<span className="asteric">*</span></span>
            <input
              type="text"
              placeholder="Enter..."
              value={formData.comments}
              onChange={(event) => updateField('comments', event.target.value)}
              required
            />
          </label>
        </div>

        <div className="add-effort-inline-actions">
          <button type="button" className="ghost" onClick={handleCancelEditing}>Cancel</button>
          <button type="button" className="primary" onClick={handleAddRow}>{ editingRowId ? 'Update' : 'Add' }</button>
        </div>

        <div className="add-effort-section">
          <h4>Yet to Submit Effort</h4>
          <EffortGrid columns={yetToSubmitColumns} rows={yetToSubmitRows} />
        </div>

        <div className="already-added-effort">
          <div className="already-added-effort-head">
            <h4>Already Logged Effort</h4>
            <span>{loggedDateLabel}</span>
          </div>
          {loading && <div className="add-effort-loading">Loading...</div>}
          <EffortGrid columns={alreadyLoggedColumns} rows={alreadyLoggedRows} className="already-added-grid" />
        </div>

        <div className="add-effort-footer">
          <button type="button" className="ghost" onClick={handleModalClose}>Cancel</button>
          <button type="button" className="primary" onClick={handleSubmit}>Submit</button>
        </div>
      </div>
    </div>
  );
};

export default AddEffortModal;
